import type { AxiosRequestConfig } from 'axios';
import api from './api';
import { Vehicle } from '../types';
import axios from 'axios';

/**
 * O axios vem com Content-Type: application/json por padrão.
 * Para FormData, NÃO pode forçar "multipart/form-data" sem boundary — o Laravel
 * não recebe o arquivo e responde validation.uploaded.
 */
const formDataPostConfig: AxiosRequestConfig = {
  transformRequest: [
    (data, headers) => {
      if (data instanceof FormData) {
        if (headers && typeof (headers as { delete?: (k: string) => void }).delete === 'function') {
          (headers as { delete: (k: string) => void }).delete('Content-Type');
        } else if (headers && typeof headers === 'object') {
          delete (headers as Record<string, unknown>)['Content-Type'];
        }
      }
      return data;
    },
  ],
};

const vehicleService = {
  /**
   * Busca todos os veículos
   */
  getAllVehicles: async (): Promise<Vehicle[]> => {
    try {
      const response = await api.get('/vehicles');
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
      throw error;
    }
  },

  /**
   * Busca veículos disponíveis (não vendidos)
   */
  getAvailableVehicles: async (): Promise<Vehicle[]> => {
    try {
      const response = await api.get('/vehicles/available');
      return response.data || [];
    } catch (error) {
      // Se o endpoint específico não existir, filtrar manualmente
      try {
        const allVehicles = await vehicleService.getAllVehicles();
        return allVehicles.filter(vehicle => vehicle.status === 'available');
      } catch (innerError) {
        console.error('Erro ao buscar veículos disponíveis:', innerError);
        throw innerError;
      }
    }
  },

  /**
   * Busca um veículo específico
   * @param id ID do veículo
   */
  getVehicle: async (id: number): Promise<Vehicle> => {
    try {
      const response = await api.get(`/vehicles/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar veículo ${id}:`, error);
      throw error;
    }
  },

  /**
   * Cria um novo veículo
   * @param vehicle Dados do veículo
   */
  createVehicle: async (vehicle: any): Promise<Vehicle> => {
    try {


      // Assegurar que os campos numéricos sejam números
      // const formattedVehicle = {
      //   ...vehicle,
      //   purchase_price: typeof vehicle.purchase_price === 'string' 
      //     ? parseFloat(String(vehicle.purchase_price).replace(/[^\d.-]/g, '')) 
      //     : vehicle.purchase_price,
      //   year: typeof vehicle.year === 'string' 
      //     ? parseInt(String(vehicle.year)) 
      //     : vehicle.year
      // };


      api.defaults.headers['Content-Type'] = 'multipart/form-data'

      const response = await api.post('/vehicles', vehicle);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar veículo:', error);
      throw error;
    }
  },

  uploadFile: async (form: FormData): Promise<any> => {
    try {
      const uploadTimeoutMs = 120_000;
      const arquivo = form.get('arquivo');
      const file = arquivo instanceof File ? arquivo : null;

      const ext = file?.name.split('.').pop()?.toLowerCase();
      const mime = file?.type.toLowerCase() ?? '';
      const isHeic =
        ext === 'heic' ||
        ext === 'heif' ||
        mime.includes('heic') ||
        mime.includes('heif');

      if (isHeic) {
        const apiConvert = axios.create({
          baseURL: 'https://convert.carmanager.com.br',
          headers: {
            'x-api-key': 'asd',
          },
        });
        const response = await apiConvert.post('convert', form, {
          timeout: uploadTimeoutMs,
          ...formDataPostConfig,
        });
        return response.data;
      }

      const response = await api.post('/upload/public', form, {
        timeout: uploadTimeoutMs,
        ...formDataPostConfig,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao enviar arquivo do veículo:', error);
      throw error;
    }
  },


  /**
   * Atualiza um veículo existente
   * @param id ID do veículo
   * @param vehicle Dados atualizados do veículo
   */
  updateVehicle: async (id: number, vehicle: Partial<Vehicle> | FormData): Promise<Vehicle> => {
    try {
      const response = await api.post(`/vehicles/editar/${id}`, vehicle, formDataPostConfig);
      return response.data;

    } catch (error) {
      console.error(`Erro ao atualizar veículo ${id}:`, error);
      throw error;
    }
  },

  /**
   * Exclui um veículo
   * @param id ID do veículo
   */
  deleteVehicle: async (id: number): Promise<number> => {
    try {
      const response = await api.delete(`/vehicles/${id}`);
      return response.status
    } catch (error) {
      console.error(`Erro ao excluir veículo ${id}:`, error);
      throw error;
    }
  },

  /**
 * Exclui um veículo
 * @param id ID do veículo
 *    * @param id_image ID da imagemn

 */
  deleteImageVehicle: async (id: number, id_image: number) => {
    try {
      const response = await api.delete(`/vehicles/${id}/images/${id_image}`);

      return response;

    } catch (error) {
      console.error(`Erro ao excluir veículo ${id}:`, error);
      throw error;
    }
  },
  deleteDocVehicle: async (id: number, id_doc: number) => {
    try {
      const response = await api.delete(`/vehicles/${id}/document/${id_doc}`);

      return response;

    } catch (error) {
      console.error(`Erro ao excluir veículo ${id}:`, error);
      throw error;
    }
  },
  /**
   * Atualiza o status de um veículo
   * @param id ID do veículo
   * @param status Novo status do veículo
   */
  updateVehicleStatus: async (id: number, status: 'available' | 'sold' | 'maintenance'): Promise<Vehicle> => {
    try {
      // Tentar usar o endpoint específico para atualizar status
      try {
        const response = await api.patch(`/vehicles/${id}/status`, { status });
        return response.data;
      } catch (patchError) {
        // Se o endpoint específico falhar, usar a atualização completa
        const vehicle = await vehicleService.getVehicle(id);
        return await vehicleService.updateVehicle(id, { ...vehicle, status });
      }
    } catch (error) {
      console.error(`Erro ao atualizar status do veículo ${id}:`, error);
      throw error;
    }
  },


};

export default vehicleService; 